import { Outlet, createRootRoute, HeadContent, Scripts } from "@tanstack/react-router";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";

import appCss from "../styles.css?url";
import { NotFoundView } from "./not-found";
import i18nInstance, { applyDirection, readCookieLang, readStoredLang } from "@/lib/i18n";
import { resolveLang } from "@/lib/lang.isomorphic";


function NotFoundComponent() {
  return <NotFoundView />;
}




export const Route = createRootRoute({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { title: "Spayzo — Book inspiring spaces by the hour" },
      { name: "description", content: "Spayzo is a SaaS platform for booking spaces by the hour, with add-ons bundled in one seamless transaction." },
      { name: "author", content: "Spayzo" },
      { property: "og:site_name", content: "Spayzo" },
      { property: "og:title", content: "Spayzo — Book inspiring spaces by the hour" },
      { property: "og:description", content: "Discover studios, offices, event halls and chalets — bookable by the hour with add-ons bundled in." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:site", content: "@Spayzo" },
      { name: "twitter:title", content: "Spayzo — Book inspiring spaces by the hour" },
      { name: "twitter:description", content: "Discover studios, offices, event halls and chalets — bookable by the hour with add-ons bundled in." },
    ],
    links: [
      {
        rel: "stylesheet",
        href: appCss,
      },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          name: "Spayzo",
          url: "https://spayzo.lovable.app",
          logo: "https://spayzo.lovable.app/favicon.ico",
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebSite",
          name: "Spayzo",
          url: "https://spayzo.lovable.app",
          potentialAction: {
            "@type": "SearchAction",
            target: "https://spayzo.lovable.app/discover?q={search_term_string}",
            "query-input": "required name=search_term_string",
          },
        }),
      },
    ],
  }),
  beforeLoad: () => {
    // Resolve the language from the cookie on both server and client so the
    // SSR markup and the first client render use the same strings/direction.
    const lang = resolveLang();
    if (i18nInstance.language !== lang) {
      void i18nInstance.changeLanguage(lang);
    }
  },
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
});

function RootShell({ children }: { children: React.ReactNode }) {
  const { i18n } = useTranslation();
  const lang = i18n.language === "ar" ? "ar" : "en";
  return (
    <html lang={lang} dir={lang === "ar" ? "rtl" : "ltr"} suppressHydrationWarning>
      <head>
        <HeadContent />
      </head>
      <body suppressHydrationWarning>
        {children}
        <Scripts />
      </body>
    </html>
  );
}


function RootComponent() {
  const { i18n } = useTranslation();

  // Cookies can be blocked in embedded/preview contexts, so fall back to
  // localStorage — but only after the page has fully loaded, well after
  // hydration, so it can never cause a hydration mismatch.
  useEffect(() => {
    const apply = () => {
      const stored = readCookieLang() === "ar" ? "ar" : readStoredLang();
      if (i18n.language !== stored) {
        void i18n.changeLanguage(stored);
      }
      applyDirection(stored);
    };
    if (document.readyState === "complete") {
      const id = window.setTimeout(apply, 0);
      return () => window.clearTimeout(id);
    }
    window.addEventListener("load", apply);
    return () => window.removeEventListener("load", apply);
  }, [i18n]);




  return <Outlet />;
}

